// import Pagination from '../../../../../components/Shared/Pagination/Pagination';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import useGetOnhandInventoryItemsByInventoryId from '@hooks/INV/MasterItems/useGetOnhandInventoryItemsByInventoryId';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import { TreeSelect } from 'antd';
import { Card, CardBody } from '@components/card/card';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import ToastSwal from '@components/Shared/Alert/ToastSwal';
// import { FixedSizeList as List } from 'react-window';

interface LinesProps {
  formik: FormikProps<any>;
}

const ItemsList: React.FC<LinesProps> = ({ formik }) => {
  const { values } = formik;
  const { t, i18n } = useTranslation();

  const { TreeSubInventoryData, loading: loading3, error: error3 } = useGetSubinventoryList();
  const { InventoryItemsOptions, loading, error } = useGetOnhandInventoryItemsByInventoryId({
    InventoryId: values.inventoryId
  });

  // حالة التحميل للإجراءات
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const handleAddLine = async () => {
    const newLine: any = {
      id: null,
      subInventoryId: null,
      inventoryItemId: null,
      systemQuantity: 0,
      description: ''
    };

    await formik.setFieldValue('inventoryCountItemsList', [...formik.values.inventoryCountItemsList, newLine]);

    // تمرير التركيز إلى أول حقل في الصف الجديد
    setTimeout(() => {
      const newRowIndex = formik.values.inventoryCountItemsList.length;
      const firstInput = document.querySelector(`[data-row-index="${newRowIndex}"]`);
      if (firstInput) {
        (firstInput as HTMLElement).focus();
      }
    }, 100);
  };

  const handleLineChange = async (index: number, field: string, value: any) => {
    setActionLoading(true);

    const updatedLines = [...formik.values.inventoryCountItemsList];

    if (field === 'inventoryItemId') {
      const selectedItem = InventoryItemsOptions?.find(f => f.value === value);
      const systemQuantity = selectedItem?.isPrimaryUOM ? selectedItem.StockQty : 0;

      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value,
        systemQuantity,
        description: selectedItem?.label || ''
      };
    } else {
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value
      };
    }

    await formik.setFieldValue('inventoryCountItemsList', updatedLines);
    setActionLoading(false);
  };

  const handleDeleteLine = async (index: number) => {
    const updatedLines = formik.values.inventoryCountItemsList?.filter((_: any, i: number) => i !== index);
    await formik.setFieldValue('inventoryCountItemsList', updatedLines);
  };

  const handleAddAllItems = async () => {
    if (!InventoryItemsOptions || InventoryItemsOptions.length === 0) {
      ToastSwal({ text: 'لا توجد أصناف متاحة للإضافة' });
      return;
    }

    setActionLoading(true);

    try {
      const uniqueItems = InventoryItemsOptions?.filter(
        (item, idx, self) => idx === self?.findIndex(i => i.itemId === item.itemId)
      );

      const currentItems = formik.values.inventoryCountItemsList || [];

      const newItems = uniqueItems
        .filter(newItem => !currentItems.some((existingItem: any) => existingItem.inventoryItemId === newItem.value))
        .map(item => ({
          id: null,
          subInventoryId: null,
          inventoryItemId: item.value,
          systemQuantity: item.StockQty || 0,
          description: item.label || ''
        }));

      if (newItems.length === 0) {
        ToastSwal({ text: 'جميع الأصناف مضافة مسبقاً' });
        return;
      }

      await formik.setFieldValue('inventoryCountItemsList', [...currentItems, ...newItems]);

      // عرض رسالة نجاح
      ToastSwal({ text: `تم اضافة ${newItems.length} صنف بنجاح` });
    } catch (error) {
      ToastSwal({ text: 'حدث خطأ أثناء إضافة الأصناف' });
    } finally {
      setActionLoading(false);
    }
  };

  // حساب الإحصائيات
  const stats = {
    totalItems: formik.values.inventoryCountItemsList?.length || 0,
    itemsWithQuantity: formik.values.inventoryCountItemsList?.filter((item: any) => item.systemQuantity > 0).length || 0
  };

  return (
    <>
      <Loading loading={loading || actionLoading} error={error} Type='Dots'>
        <div className='mb-3'>
          {/* بطاقة الإحصائيات */}
          <div className='row mb-3'>
            <div className='col-12'>
              <Card className='mx-3'>
                <div className='card bg-secondary bg-opacity-50'>
                  <CardBody className='  py-3'>
                    <div className='row text-center'>
                      <div className='col-md-4'>
                        <small className='text-muted'>{t('إجمالي الأصناف')}</small>
                        <div className='h5 mb-0 text-theme'>{stats.totalItems}</div>
                      </div>
                      <div className='col-md-4'>
                        <small className='text-muted'>{t('أصناف لها رصيد')}</small>
                        <div className='h5 mb-0 text-success'>{stats.itemsWithQuantity}</div>
                      </div>
                      <div className='col-md-4'>
                        <small className='text-muted'>{t('الأصناف المتاحة')}</small>
                        <div className='h5 mb-0 text-info'>{InventoryItemsOptions?.length || 0}</div>
                      </div>
                    </div>
                  </CardBody>
                </div>
              </Card>
            </div>
          </div>

          {/* أزرار التحكم */}
          <div className='row mb-3'>
            <div className='col-12 text-end'>
              <div className='btn-group mx-3' role='group'>
                <button
                  type='button'
                  className='btn btn-outline-theme'
                  onClick={handleAddLine}
                  disabled={actionLoading}
                >
                  <i className='fas fa-plus me-1'></i>
                  {t('إضافة صنف')}
                </button>
                <button
                  type='button'
                  className='btn btn-outline-success'
                  onClick={handleAddAllItems}
                  disabled={actionLoading || !InventoryItemsOptions?.length}
                >
                  <i className='fas fa-layer-group me-1'></i>
                  {t('إضافة جميع الأصناف')}
                </button>
              </div>
            </div>
          </div>

          {/* الجدول */}
          <div
            className={`table-responsive rounded-0 border ${
              formik.errors.inventoryCountItemsList ? 'border-2 border-danger' : 'border-1'
            }`}
          >
            <div style={{ maxHeight: '450px' }} className='scrollable-table'>
              <table className='table table-borderless table-hover mb-0'>
                <thead
                  className='bg-theme bg-opacity-30 text-nowrap sticky-top'
                  style={{
                    backdropFilter: 'blur(15px)'
                  }}
                >
                  <tr>
                    <th className='align-middle text-center' style={{ width: '3%' }}>
                      #
                    </th>
                    <th className='align-middle' style={{ width: '20%' }}>
                      {t('المخزن الفرعي')}
                    </th>
                    <th className='align-middle' style={{ width: '38%' }}>
                      {t('اسم الصنف')}
                    </th>
                    <th className='align-middle text-center' style={{ width: '6%' }}>
                      {t('الرصيد في النظام')}
                    </th>
                    <th className='align-middle text-center' style={{ width: '3%' }}>
                      {t('الإجراءات')}
                    </th>
                  </tr>
                </thead>
                <tbody className='fw-bold'>
                  {formik.values.inventoryCountItemsList?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className='text-center py-4 text-muted'>
                        <i className='fas fa-inbox fa-2x mb-2'></i>
                        <div>{t('لا توجد أصناف مضافة')}</div>
                        <small className='text-muted'>{t('انقر على "إضافة صنف" لبدء الإضافة')}</small>
                      </td>
                    </tr>
                  ) : (
                    formik.values.inventoryCountItemsList
                      ?.slice()
                      ?.reverse()
                      ?.map((line: any, index: any) => {
                        const originalIndex = formik.values.inventoryCountItemsList?.length - 1 - index;
                        const displayNumber = formik.values.inventoryCountItemsList?.length - index;

                        return (
                          <tr key={`line-${originalIndex}`} className={!line.inventoryItemId ? 'table-warning' : ''}>
                            <td className='align-middle bg-theme bg-opacity-30 small text-center'>{displayNumber}</td>
                            <td className='align-middle small'>
                              <TreeSelect
                                data-row-index={originalIndex}
                                loading={loading3}
                                showSearch
                                className='w-100'
                                treeLine
                                placeholder={t('اختر المخزن الفرعي')}
                                allowClear
                                treeDefaultExpandAll={false}
                                value={line.subInventoryId}
                                onChange={option =>
                                  handleLineChange(originalIndex, 'subInventoryId', option === null ? null : option)
                                }
                                treeData={TreeSubInventoryData?.filter(f => f.parentId === values.inventoryId)}
                                treeNodeFilterProp='title'
                                disabled={actionLoading}
                              />
                            </td>

                            <td className='align-middle small'>
                              <Select
                                data-row-index={originalIndex}
                                classNamePrefix='react-select'
                                className='w-100'
                                isSearchable
                                isClearable
                                options={InventoryItemsOptions?.filter(
                                  (item, idx, self) => idx === self?.findIndex(i => i.itemId === item.itemId)
                                )?.filter(
                                  f =>
                                    !formik.values.inventoryCountItemsList?.some(
                                      (line: any) => line.inventoryItemId === f.value
                                    ) || f.value === line.inventoryItemId
                                )}
                                onChange={option =>
                                  handleLineChange(
                                    originalIndex,
                                    'inventoryItemId',
                                    option === null ? null : option.value
                                  )
                                }
                                value={
                                  line.inventoryItemId === null
                                    ? null
                                    : InventoryItemsOptions?.find(option => option.value === line.inventoryItemId)
                                }
                                placeholder={t('اختر الصنف')}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                  control: base => ({
                                    ...base,
                                    borderColor: !line.inventoryItemId ? '#ffc107' : base.borderColor
                                  })
                                }}
                                formatOptionLabel={option => (
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <div className='text-truncate' title={option.label}>
                                      {option.label}
                                    </div>
                                    {option.barcode && (
                                      <div className='text-theme fw-bold small me-2'>{option.barcode}</div>
                                    )}
                                  </div>
                                )}
                                isDisabled={actionLoading}
                              />
                            </td>

                            <td className='align-middle small'>
                              <input
                                type='text'
                                autoComplete='off'
                                className={`form-control text-center px-0 fw-bold ${
                                  line.systemQuantity > 0 ? 'text-success' : 'text-muted'
                                }`}
                                value={line.systemQuantity || 0}
                                readOnly
                                tabIndex={-1}
                              />
                            </td>

                            <td className='align-middle small text-center'>
                              <button
                                type='button'
                                className='btn btn-outline-danger btn-sm'
                                onClick={() => handleDeleteLine(originalIndex)}
                                disabled={actionLoading}
                                title={t('حذف الصنف')}
                              >
                                <i className='fas fa-trash-alt'></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* رسائل الخطأ */}
          {formik.errors.inventoryCountItemsList && (
            <div className='text-danger small mt-2'>
              <i className='fas fa-exclamation-triangle me-1'></i>
              {typeof formik.errors.inventoryCountItemsList === 'string'
                ? formik.errors.inventoryCountItemsList
                : t('يوجد خطأ في قائمة الأصناف')}
            </div>
          )}
        </div>
      </Loading>

      <style>{`
        .scrollable-table {
          overflow-y: auto;
          scrollbar-width: thin;
        }
        .scrollable-table::-webkit-scrollbar {
          width: 6px;
        }
        .scrollable-table::-webkit-scrollbar-thumb {
          background-color: #ccc;
          border-radius: 3px;
        }
      `}</style>
    </>
  );
};

export default ItemsList;
